import { useCallback, useState } from "react";

interface UseClipboardOptions {
  timeout?: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseClipboardReturn {
  isCopied: boolean;
  copy: (text: string) => Promise<void>;
  error: Error | null;
}

export const useClipboard = (
  options: UseClipboardOptions = {},
): UseClipboardReturn => {
  const { timeout = 1000, onSuccess, onError } = options;
  const [isCopied, setIsCopied] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const copy = useCallback(
    async (text: string) => {
      if (!text) {
        const err = new Error("Text to copy is empty");
        setError(err);
        onError?.(err);
        return;
      }

      try {
        // Try modern Clipboard API first
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for older browsers or non-secure contexts
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          textArea.style.top = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          const successful = document.execCommand("copy");
          document.body.removeChild(textArea);

          if (!successful) {
            throw new Error("Failed to copy text using fallback method");
          }
        }

        setIsCopied(true);
        setError(null);
        onSuccess?.();

        // Reset copied state after timeout
        setTimeout(() => {
          setIsCopied(false);
        }, timeout);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to copy text");
        setError(error);
        setIsCopied(false);
        onError?.(error);
      }
    },
    [timeout, onSuccess, onError],
  );

  return { isCopied, copy, error };
};
